/**
 * tasksView.ts — pure view-model helpers for Operations / Field Schedule
 * (M5a).
 *
 * The wire stores task type and status as lowercase slugs (0103's CHECKs);
 * this module owns the labels — the Add Task modal's five REAL options
 * (klaxiora-crm-mockup-full.html :2643-2647; the test-task dev artifact
 * :2642 is excluded by decision M5a) — the date grouping the interim list
 * renders, and the option/reference lines the incident link reuses
 * (:5033's "type — assignee (date)" form). No KPI helpers here BY DESIGN:
 * the Team Tracker is M5b surface and M6 reads the entity server-side.
 */
import type { OpsTask } from '../../types'

export const TASK_TYPES = [
  { slug: 'client_visit',     label: 'Client Visit' },
  { slug: 'product_demo',     label: 'Product Demo' },
  { slug: 'contract_signing', label: 'Contract Signing' },
  { slug: 'site_survey',      label: 'Site Survey' },
  { slug: 'follow_up_call',   label: 'Follow-up Call' },
] as const

export function taskTypeLabel(slug: string): string {
  return TASK_TYPES.find(t => t.slug === slug)?.label ?? slug
}

/** The header count line, the sibling-module wording pattern. */
export function taskCountLine(n: number): string {
  return `${n} task${n !== 1 ? 's' : ''} on the schedule.`
}

/** Group-header date: weekday + full date ("Monday, 7 September 2026") —
 *  a schedule reads by day of week. Plain "YYYY-MM-DD" in, '—' on junk. */
export function formatTaskDay(date: string): string {
  const d = new Date(`${date}T00:00:00`)
  if (isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}

export interface TaskDayGroup {
  date: string
  tasks: OpsTask[]
}

/** Group the server-ordered list (scheduled_date ASC, timed before untimed)
 *  into per-day sections, preserving that order — the interim list's shape
 *  until M5b's calendar takes over as the primary view. */
export function groupTasksByDate(tasks: OpsTask[]): TaskDayGroup[] {
  const groups: TaskDayGroup[] = []
  for (const t of tasks) {
    const last = groups[groups.length - 1]
    if (last && last.date === t.scheduledDate) last.tasks.push(t)
    else groups.push({ date: t.scheduledDate, tasks: [t] })
  }
  return groups
}

/** Option line for the incident drawer's Related-task select — the mockup's
 *  "type — assignee (date)" form (:5033), with the honest Unassigned blank. */
export function taskOptionLabel(t: OpsTask): string {
  return `${taskTypeLabel(t.taskType)} — ${t.assignedToName || 'Unassigned'} (${t.scheduledDate})`
}

/** Read-line for an incident's joined related-task fields ("Site Survey —
 *  4 September 2026"); '' when unlinked so callers render nothing — never
 *  a dash column of fabricated links. */
export function relatedTaskLine(taskType?: string, taskDate?: string): string {
  if (!taskType || !taskDate) return ''
  const d = new Date(`${taskDate}T00:00:00`)
  const day = isNaN(d.getTime()) ? taskDate : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
  return `${taskTypeLabel(taskType)} — ${day}`
}

// ── Team Tracker (M5b — mockup :1911-1920 / renderTeamTracker :3791-3825) ────
//
// Computed over the tasks the caller scopes (the calendar passes the
// DISPLAYED month's tasks, so the card's month claim is true — the mockup
// computes over all tasks under a "this month" subtitle). Grouped by
// assignedTo ID (display via assignedToName), so the team KPI equals
// overviewView's teamWithTasks formula; Unassigned is excluded from rows
// and the team count (the mockup's computeAgentTaskCounts rule :3783) but
// still counts in the task totals — the totals describe the schedule, the
// bars describe people.

export interface TrackerRow {
  name: string
  total: number
  completed: number
  /** Bar width: this person's share of the BUSIEST person's total, 0-100
   *  (the mockup's total/maxTotal rule :3794-3806). */
  share: number
}

export interface TrackerStats {
  total: number
  completed: number
  scheduled: number
  team: number
  rows: TrackerRow[]
}

export function teamTracker(tasks: OpsTask[]): TrackerStats {
  const byId = new Map<string, { name: string; total: number; completed: number }>()
  for (const t of tasks) {
    if (!t.assignedTo) continue
    const cur = byId.get(t.assignedTo) ?? { name: t.assignedToName || 'Unnamed', total: 0, completed: 0 }
    cur.total++
    if (t.status === 'completed') cur.completed++
    byId.set(t.assignedTo, cur)
  }
  const max = Math.max(1, ...[...byId.values()].map(c => c.total))
  const rows = [...byId.values()]
    .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
    .map(c => ({ name: c.name, total: c.total, completed: c.completed, share: Math.round((c.total / max) * 100) }))
  const completed = tasks.filter(t => t.status === 'completed').length
  return { total: tasks.length, completed, scheduled: tasks.length - completed, team: rows.length, rows }
}

/** The mockup's avatar initials rule (:3800). */
export function trackerInitials(name: string): string {
  return name.split(' ').map(p => p[0] ?? '').join('').toUpperCase()
}
